import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  CalendarDays,
  Repeat,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

// Import modular components
import {
  SchedulingStats,
  CalendarGrid,
  VisitDetailModal,
  CreateVisitDialog,
  TemplateCard,
  CreateTemplateDialog,
  GenerateDialog,
} from '../components/scheduling';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Scheduling() {
  const { organization } = useAuth();
  const [activeTab, setActiveTab] = useState('calendar');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [filterStaff, setFilterStaff] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [quickAddDate, setQuickAddDate] = useState('');

  // Recurring templates state
  const [templates, setTemplates] = useState([]);
  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    start_date: '',
    end_date: '',
  });

  // Calculate week dates
  const weekDates = useMemo(() => {
    const dates = [];
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [currentDate]);

  const weekStart = weekDates[0].toISOString().split('T')[0];
  const weekEnd = weekDates[6].toISOString().split('T')[0];

  useEffect(() => {
    if (organization?.id) {
      fetchData();
    }
  }, [organization, weekStart, weekEnd]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [entriesRes, staffRes, clientsRes, statsRes, templatesRes] = await Promise.all([
        axios.get(`${API_URL}/api/rota`, {
          params: { organization_id: organization?.id, start_date: weekStart, end_date: weekEnd }
        }),
        axios.get(`${API_URL}/api/staff`, { params: { organization_id: organization?.id } }),
        axios.get(`${API_URL}/api/clients`, { params: { organization_id: organization?.id } }),
        axios.get(`${API_URL}/api/rota/stats/weekly`, { 
          params: { organization_id: organization?.id, start_date: weekStart }
        }),
        axios.get(`${API_URL}/api/recurring-templates`, { params: { organization_id: organization?.id } })
      ]);

      setEntries(entriesRes.data);
      setStaff(staffRes.data);
      setClients(clientsRes.data);
      setStats(statsRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  // Navigation handlers
  const handlePrevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const handleToday = () => setCurrentDate(new Date());

  // Visit handlers
  const handleCreateEntry = async (formData) => {
    if (!formData.staff_id || !formData.client_id || !formData.scheduled_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/rota`, {
        organization_id: organization?.id,
        ...formData
      });
      toast.success('Visit scheduled successfully');
      setIsCreateOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error creating entry:', error);
      toast.error('Failed to schedule visit');
    }
  };

  const handleUpdateStatus = async (entryId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/rota/${entryId}/status`, null, {
        params: { status: newStatus }
      });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this visit?')) return;

    try {
      await axios.delete(`${API_URL}/api/rota/${entryId}`);
      toast.success('Visit deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete visit');
    }
  };

  // Template handlers
  const handleCreateTemplate = async (templateForm) => {
    if (!templateForm.name || !templateForm.staff_id || !templateForm.client_id || templateForm.days_of_week.length === 0) {
      toast.error('Please fill in all required fields and select at least one day');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/recurring-templates`, {
        organization_id: organization?.id,
        ...templateForm
      });
      toast.success('Recurring template created');
      setIsTemplateOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
    }
  };

  const handleToggleTemplateActive = async (template) => {
    try {
      await axios.put(`${API_URL}/api/recurring-templates/${template.id}`, {
        is_active: !template.is_active
      });
      toast.success(template.is_active ? 'Template paused' : 'Template activated');
      fetchData();
    } catch (error) {
      console.error('Error toggling template:', error);
      toast.error('Failed to update template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await axios.delete(`${API_URL}/api/recurring-templates/${templateId}`);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleGenerateFromTemplate = async (templateId) => {
    if (!generateForm.start_date || !generateForm.end_date) {
      toast.error('Please select start and end dates');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/recurring-templates/${templateId}/generate`,
        null,
        { params: { start_date: generateForm.start_date, end_date: generateForm.end_date } }
      );
      toast.success(`Generated ${response.data.entries_created} visits`);
      setIsGenerateOpen(false);
      setSelectedTemplate(null);
      setGenerateForm({ start_date: '', end_date: '' });
      fetchData();
    } catch (error) {
      console.error('Error generating visits:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate visits');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateAll = async () => {
    if (!generateForm.start_date || !generateForm.end_date) {
      toast.error('Please select start and end dates');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/recurring-templates/generate-all`,
        null,
        { params: { organization_id: organization?.id, start_date: generateForm.start_date, end_date: generateForm.end_date } }
      );
      toast.success(`Generated ${response.data.total_entries_created} visits from ${response.data.templates_processed} templates`);
      setIsGenerateOpen(false);
      setGenerateForm({ start_date: '', end_date: '' });
      fetchData();
    } catch (error) {
      console.error('Error generating visits:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate visits');
    } finally {
      setGenerating(false);
    }
  };

  const handleQuickAdd = (date) => {
    setQuickAddDate(date.toISOString().split('T')[0]);
    setIsCreateOpen(true);
  };

  const activeTemplatesCount = templates.filter(t => t.is_active).length;

  return (
    <div className="min-h-screen" data-testid="scheduling-page">
      <Header title="Staff Scheduling" />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <SchedulingStats stats={stats} />

        {/* Tabs for Calendar and Templates */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="calendar" className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4" />
              Weekly Calendar
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center gap-2">
              <Repeat className="w-4 h-4" />
              Recurring Templates
            </TabsTrigger>
          </TabsList>

          {/* Calendar Tab */}
          <TabsContent value="calendar" className="space-y-4">
            {/* Controls */}
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handlePrevWeek}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleToday}>
                      Today
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleNextWeek}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    <span className="ml-2 font-medium">
                      {formatDate(weekDates[0])} - {formatDate(weekDates[6])}
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    <Select value={filterStaff} onValueChange={setFilterStaff}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Filter by Staff" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Staff</SelectItem>
                        {staff.map(s => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.user?.first_name} {s.user?.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={filterClient} onValueChange={setFilterClient}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Filter by Client" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Clients</SelectItem>
                        {clients.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.first_name} {c.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <CreateVisitDialog
                      isOpen={isCreateOpen}
                      onOpenChange={setIsCreateOpen}
                      staff={staff}
                      clients={clients}
                      initialDate={quickAddDate}
                      onSubmit={handleCreateEntry}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Calendar Grid */}
            <CalendarGrid
              weekDates={weekDates}
              entries={entries}
              filterStaff={filterStaff}
              filterClient={filterClient}
              loading={loading}
              onVisitClick={setSelectedEntry}
              onQuickAdd={handleQuickAdd}
            />
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold">Recurring Visit Templates</h2>
                <p className="text-sm text-slate-500">Create patterns to auto-generate weekly schedules</p>
              </div>
              <div className="flex gap-2">
                <GenerateDialog
                  isOpen={isGenerateOpen && !selectedTemplate}
                  onOpenChange={(open) => { setIsGenerateOpen(open); if (!open) setSelectedTemplate(null); }}
                  generating={generating}
                  generateForm={generateForm}
                  setGenerateForm={setGenerateForm}
                  onGenerate={handleGenerateAll}
                  activeTemplatesCount={activeTemplatesCount}
                  isBulk={true}
                />

                <CreateTemplateDialog
                  isOpen={isTemplateOpen}
                  onOpenChange={setIsTemplateOpen}
                  staff={staff}
                  clients={clients}
                  onSubmit={handleCreateTemplate}
                />
              </div>
            </div>

            {/* Templates List */}
            {templates.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map(template => (
                  <React.Fragment key={template.id}>
                    <TemplateCard
                      template={template}
                      onToggleActive={handleToggleTemplateActive}
                      onGenerateClick={(t) => { setSelectedTemplate(t); setIsGenerateOpen(true); }}
                      onDelete={handleDeleteTemplate}
                    />
                    <GenerateDialog
                      isOpen={isGenerateOpen && selectedTemplate?.id === template.id}
                      onOpenChange={(open) => { setIsGenerateOpen(open); if (!open) setSelectedTemplate(null); }}
                      templateName={template.name}
                      generating={generating}
                      generateForm={generateForm}
                      setGenerateForm={setGenerateForm}
                      onGenerate={() => handleGenerateFromTemplate(template.id)}
                      isBulk={false}
                    />
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Repeat className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No recurring templates yet</p>
                  <p className="text-sm text-slate-400 mt-1">Create a template to auto-generate weekly schedules</p>
                  <Button className="mt-4 bg-teal-600 hover:bg-teal-700" onClick={() => setIsTemplateOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Create Template
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Visit Detail Modal */}
        <VisitDetailModal
          entry={selectedEntry}
          isOpen={!!selectedEntry}
          onClose={() => setSelectedEntry(null)}
          onUpdateStatus={handleUpdateStatus}
          onDelete={handleDeleteEntry}
        />
      </div>
    </div>
  );
}
